import * as _ from "lodash";
import { BaseCollection } from "../../../collections/_BaseCollection";
import { DashboardCollection } from "../../../collections/Dashboards";
import { InstallCollection } from "../../../collections/Installs";
import { PermissionFunction } from "../../../collections/Permissions";
import { ReportsCollection } from "../../../collections/Reports";
import { BaseList, TableProperties } from "../../_BaseList";
import { BaseDetailAccountView } from "../_BaseAccountViews";
import { TeamDetailViewOptions } from "./TeamDetailViews";

type PermissionFunctionOrString = PermissionFunction | string;

interface PermissionNameMapEntry {
    name: string;
    func: PermissionFunction;
    id?: () => number,
    info?: string;
    provides?: { [referenceKey: string]: PermissionFunctionOrString[] };
    requires?: PermissionFunction[];
}

interface PermissionNameMap {
    createPermission?: PermissionNameMapEntry,
    modelPermissions: PermissionNameMapEntry[]
}

export abstract class BasePermissionView extends BaseDetailAccountView {
    options: TeamDetailViewOptions;

    abstract getTitle(): string

    abstract getFirstColumnName(): string

    abstract getFirstColumnValue(model): string

    abstract getCollection(): BaseCollection<Backbone.Model>

    abstract getUrlAddon(): string

    abstract getPermissionNameMap(model?): PermissionNameMap
}

export abstract class BasePermissionPreview extends BasePermissionView {

    show() {
        var baseList = new BaseList(this.options);
        var props: TableProperties = {
            tableEmptyText: 'No ' + this.getTitle() + ' Assigned',
            columnProperties: {
                [this.getFirstColumnName()]: {
                    customColumn: {
                        templateFunction: _.template('' +
                            ' <div class="col-md-5 col-sm-5 col-xs-12">' +
                            ' <%- getFirstColumnValue(mod) %>' +
                            ' </div>'),
                        variables: {
                            getFirstColumnValue: this.getFirstColumnValue.bind(this),
                        },
                    },
                    columnSizes: {'sm': '5', 'md': '5', 'xs': '12'}
                },
                'Permissions': {
                    customColumn: {
                        templateFunction: _.template(require('templates/account/team/permissionDetailsOverview.html')),
                        variables: {
                            getPermissionNameMap: this.getPermissionNameMap.bind(this),
                        }
                    },
                    columnSizes: {'sm': '7', 'md': '7', 'xs': '12'}
                }
            },
            limitedRows: {
                count: 3,
                sortBy: 'name',
                reverseSort: true,
                collectionProperties: {
                    organizationId: this.options.currentOrganizationId
                }
            },
            name: this.getTitle(),
            tableWidth: 'col-md-6 col-sm-6 col-xs-12 permission-preview',
            footer: {
                url: '#/account/' + this.options.currentOrganizationId + '/teams/' +
                this.options.currentUserGroup.get('id') + '/permissions/' + this.getUrlAddon(),
                text: 'Assign'
            }
        };

        // Filter out all models from the collection which have not at least one permission from the map
        var collection = new BaseCollection(this.getCollection().filter((m) => {
            var map = this.getPermissionNameMap(m);
            return _.some(_.map(map.modelPermissions, (e) => {
                return e.func(m.get('id'));
            }))
        }));
        this.$el.html(<any> baseList.renderCollectionList(collection, props).$el);

        return $.Deferred().resolve();
    }

    isCurrentTeamAdmin() {
        return this.options.currentUserGroup.get('is_admin') === 1;
    }
}


export class DashboardPermissionPreview extends BasePermissionPreview {

    getTitle() {
        return 'Dashboard Permissions';
    }

    getFirstColumnName() {
        return 'Dashboard';
    }

    getFirstColumnValue(model) {
        return model.get('name');
    }

    getPermissionNameMap(model) {
        var c = this.options.userGroupPermissionCollection;
        return {
            modelPermissions: [
                {name: 'View', func: c.canViewDashboard.bind(c)},
                {name: 'Modify', func: c.canModifyDashboard.bind(c)},
                {name: 'Delete', func: c.canDeleteDashboard.bind(c)},
            ]
        };
    }

    getCollection() {
        return new DashboardCollection(this.options.dashboardCollection.where({
            organization_id: this.options.currentOrganizationId
        }));
    }

    getUrlAddon() {
        return 'dashboard';
    }
}


export class UserGroupPermissionPreview extends BasePermissionPreview {

    getTitle() {
        return 'Team Permissions';
    }

    getFirstColumnName() {
        return 'Team';
    }

    getFirstColumnValue(model) {
        return model.get('name');
    }

    getPermissionNameMap(model) {
        var c = this.options.userGroupPermissionCollection;
        return {
            modelPermissions: [
                {name: 'Modify', func: c.canModifyUserGroup.bind(c)},
                {name: 'Delete', func: c.canDeleteUserGroup.bind(c)},
                {name: 'Modify Permissions', func: c.canModifyUserGroupPermissions.bind(c)},
                {name: 'Invite Users', func: c.canAddUserToUserGroup.bind(c)},
            ]
        };
    }

    getCollection() {
        return this.options.userGroupCollection;
    }

    getUrlAddon() {
        return 'team';
    }
}


export class InstallationPermissionPreview extends BasePermissionPreview {

    getPermissionNameMap(model) {
        var c = this.options.userGroupPermissionCollection;
        return {
            modelPermissions: [
                {name: 'View', func: c.canViewInstall.bind(c)},
                {name: 'View Settings', func: c.canViewSensorConfig.bind(c)},
                {name: 'Modify Settings', func: c.canModifySensorConfig.bind(c)},
            ]
        };
    }

    getTitle() {
        return 'Installation Permissions';
    }

    getFirstColumnName() {
        return 'Installation';
    }

    getFirstColumnValue(model) {
        return model.getDisplayName();
    }

    getCollection() {
        return new InstallCollection(this.options.installCollection.where({
            organization_id: this.options.currentOrganizationId
        }));
    }

    getUrlAddon() {
        return 'install';
    }
}

export class ReportPermissionPreview extends BasePermissionPreview {

    getTitle() {
        return 'Report Permissions';
    }

    getFirstColumnName() {
        return 'Report';
    }

    getFirstColumnValue(model) {
        return model.get('name');
    }

    getPermissionNameMap(model) {
        var c = this.options.userGroupPermissionCollection;
        return {
            modelPermissions: [
                {name: 'View', func: c.canViewReport.bind(c)},
                {name: 'Modify', func: c.canModifyReport.bind(c)},
                {name: 'Delete', func: c.canDeleteReport.bind(c)}
            ]
        };
    }

    getCollection() {
        return this.options.reportsCollection;
    }

    getUrlAddon() {
        return 'report';
    }
}


export abstract class BasePermissionSectionView extends BasePermissionPreview {

    protected popoverTimeouts = {};
    protected hidden: boolean = false;

    constructor(options: TeamDetailViewOptions) {
        super(<any> _.extend({}, options, {
            tagName: 'div',
            events: {
                'change .permission-checkbox': 'handlePermissionCheckboxClick',
                'change .all__permissions-toggle': 'handleAllPermissionsToggleChange',
            }
        }));
    }

    private canEditPermissions() {
        return this.options.permissionCollection.canModifyUserGroupPermissions(
            this.options.currentUserGroup.get('id')
        ) && !this.isCurrentTeamAdmin();
    }

    show() {
        var permissionEntries = this.getPermissionNameMap().modelPermissions;
        var permissionColSize = 2;
        var firstColSize = Math.max(12 - (permissionColSize * permissionEntries.length), 4);

        var columns = {
            [this.getFirstColumnName()]: {
                customColumn: {
                    templateFunction: _.template('' +
                        ' <div class="col-md-' + firstColSize + ' col-sm-' + firstColSize + ' col-xs-12"' +
                        ' data-table-cell="<%- getFirstColumnValue(mod) %>">' +
                        ' <%- getFirstColumnValue(mod) %>' +
                        ' </div>'),
                    variables: <any>  {
                        getFirstColumnValue: this.getFirstColumnValue.bind(this),
                    }
                },
                centerColumn: false,
                isPermissionsColumn: false,
                columnIndex: -1,
                columnSizes: {'sm': String(firstColSize), 'md': String(firstColSize), 'xs': '12'},
                'class': ''
            }
        };

        _.each(permissionEntries, (e, i) => {
            columns[e.name] = {
                customColumn: {
                    templateFunction: _.template('' +
                        ' <div class="col-md-' + permissionColSize + ' col-sm-' + permissionColSize + ' col-xs-12 ' +
                        ' text-center show-popover" data-table-cell="">' +
                        ' <input type="checkbox" class="permission-checkbox" ' +
                        ' data-toggle="toggle" data-on="Yes" data-off="No" data-size="mini"' +
                        ' data-checkbox-index="<%- i %>" data-model-id="<%- mod.get("id") %>"' +
                        ' <%- permissionFunc(mod.get("id")) ?  " checked" : "" %>' +
                        ' <%- !canEditPermissions ?  " disabled" : "" %>></div>'
                    ),
                    variables: <any> {
                        i: i,
                        permissionFunc: e.func.bind(this.options.userGroupPermissionCollection),
                        canEditPermissions: this.canEditPermissions()
                    }
                },
                centerColumn: true,
                isPermissionsColumn: true,
                columnIndex: i,
                columnSizes: {'sm': String(permissionColSize), 'md': String(permissionColSize), 'xs': '12'},
                'class': 'permission-popover-' + i
            };

            if (e.info) {
                columns[e.name]['popover'] = {
                    text: e.info
                };
            }
        });

        var baseList = new BaseList(this.options);
        var props: TableProperties = {
            tableEmptyText: 'No ' + this.getTitle() + ' Assigned',
            columnProperties: columns,
            tableWidth: 'col-md-12 col-sm-12 col-xs-12'
        };
        this.$('.panel-body > .row').empty().append(
            <any> baseList.renderCollectionList(this.getCollection(), props).$el
        );
        (<any> this.$('input[type="checkbox"]')).bootstrapToggle();
        (<any> this.$('[data-toggle="popover"]')).popover();
        (<any> $('input[type="checkbox"].all__permissions-toggle')).bootstrapToggle();

        this.delegateEvents();

        // Update all permissions toggle state
        const tables = $('.senseware-table');
        _.each(tables, (table) => {
            this.updateAllPermissionsToggleStates($(table));
        });

        return $.Deferred().resolve();
    }

    render() {
        var createPermission = null;
        var map = this.getPermissionNameMap();

        if (map.createPermission) {
            var createId = map.createPermission.id ? map.createPermission.id() : this.options.currentOrganizationId;

            createPermission = {
                name: map.createPermission.name,
                hasPermission: map.createPermission.func(createId),
                canEdit: this.canEditPermissions(),
            };
        }

        // Only show a save button if they can actually modify permissions
        var footer = null;
        if(this.canEditPermissions()) {
            footer = {
                button: {
                    text: 'Save',
                    class: 'btn-success save-permissions'
                }
            };
        }
        this.$el.html(this.template({
            panelTitle: {
                text: this.getTitle(),
                url: null,
                permission: createPermission
            },
            panelClass: this.getUrlAddon(),
            panelWidth: this.hidden ? 'hidden' : '',
            addButton: null,
            search: !this.options.isOverview,
            collectionLoaded: this.collectionLoaded,
            footer: footer
        }));
        this.delegateEvents();

        return this;
    }

    protected getPermissionEntryIndex(map: PermissionNameMapEntry[], func: PermissionFunction) {
        var i = -1;
        _.each(map, (e, j) => {
            if (!_.isEqual(e.func.query(0), func.query(0))) return;
            i = j;
            return false;
        });
        return i;
    }

    protected getCheckbox(mapIndex, modelId) {
        return this.$('.permission-checkbox' +
            '[data-checkbox-index="' + mapIndex + '"]' +
            '[data-model-id="' + modelId + '"]');
    }

    protected handlePopover(index) {
        clearTimeout(this.popoverTimeouts[index]);

        // Show the popover
        (<any> this.$('.permission-popover-' + index + '[data-toggle="popover"]')).popover('show');

        this.popoverTimeouts[index] = setTimeout(() => {
            (<any> this.$('.permission-popover-' + index + '[data-toggle="popover"]')).popover('hide');
            clearTimeout(this.popoverTimeouts[index]);
        }, 5 * 1000);
    }

    protected handlePermissionCheckboxClick(e) {
        e.preventDefault();
        var target = $(e.currentTarget);

        // If the create permission was clicked, don't do anything
        if (target.hasClass('create')) return;

        // Identify the parent table
        const table = target.closest(".senseware-table");
        if (!table.length) {
            console.error("Table with class 'senseware-table' not found");
            return;
        }

        var modelId = Number(target.attr('data-model-id'));
        var clickedIndex = Number(target.attr('data-checkbox-index'));
        var permissionEntries = this.getPermissionNameMap().modelPermissions;
        var clickedEntry = permissionEntries[clickedIndex];

        this.handlePopover(clickedIndex);

        if (target.is(':checked')) {
            // Check if this entry requires other permissions, because then they need to be checked
            _.each(clickedEntry.requires, (r) => {
                var requiresIndex = this.getPermissionEntryIndex(permissionEntries, r);
                var requiredCheckbox = this.getCheckbox(requiresIndex, modelId);

                if (!requiredCheckbox.is(':checked')) {
                    (<any> requiredCheckbox).bootstrapToggle('on');
                }
            });

        } else {
            // Check if this entry is required by other permissions, because then they need to be unchecked
            _.each(permissionEntries, (entry, i) => {
                if (i === clickedIndex) return;
                _.each(entry.requires, (r) => {
                    if (!_.isEqual(r.query(0), clickedEntry.func.query(0))) return;
                    var requiredCheckbox = this.getCheckbox(i, modelId);

                    if (requiredCheckbox.is(':checked')) {
                        (<any> requiredCheckbox).bootstrapToggle('off');
                    }
                })
            });
        }

        // Synchronize the "all permissions" toggle state for the current table
        this.updateAllPermissionsToggleStates(table);
    }

    protected updateAllPermissionsToggleStates(table: JQuery) {
        // Iterate over all column-level toggles in the current table
        const columns = table.find(`.all__permissions-toggle`);
        columns.each((_, toggle) => {
            const $toggle = $(toggle);
            const columnIndex = Number($toggle.attr('data-checkbox-index'));
            if (columnIndex < 0) return;
    
            // Find all checkboxes in the current column within the table
            const checkboxes = table.find(`.permission-checkbox[data-checkbox-index="${columnIndex}"]`);
            const allChecked = checkboxes.length > 0 &&
                checkboxes.toArray().every((cb) => $(cb).is(':checked'));
    
            // Temporarily destroy the bootstrap toggle to change its state without triggering the event
            ($toggle as any).bootstrapToggle('destroy');
            $toggle.prop('checked', allChecked);
            ($toggle as any).bootstrapToggle();
        });
    }

    handleAllPermissionsToggleChange(e: Event) {
        e.preventDefault();
        const target = $(e.currentTarget);
        // Get the current value of the checkbox
        const isChecked = target.is(':checked');
        // Get the index of the column where the checkbox is
        const index = Number(target.attr('data-checkbox-index'));
        if (index < 0) return;
    
        // Search for all checkboxes in the table that have the same index, excluding disabled ones
        const table = target.closest(".senseware-table");
        if (!table.length) {
            console.error("Table with class 'senseware-table' not found");
            return;
        }
    
        table.find(`.permission-checkbox[data-checkbox-index="${index}"]:not(:disabled)`).each((_, checkbox) => {
            const $checkbox = $(checkbox);
            // Bypass TypeScript type checking for bootstrapToggle
            ($checkbox as any).bootstrapToggle(isChecked ? 'on' : 'off');
        });
    }
    
    
    setHidden(hidden: boolean) {
        this.hidden = hidden;
    }

    getPermissions() {
        var map = this.getPermissionNameMap();
        var permissions = [];

        if (this.$('.permission-checkbox.create').is(':checked')) {
            var createId = map.createPermission.id ? map.createPermission.id() : this.options.currentOrganizationId;
            permissions.push(map.createPermission.func.query(createId));
            _.each(map.createPermission.provides, (funcs, referenceKey) => {
                var id = _.get(this.options, referenceKey);
                [].push.apply(permissions, _.map(funcs, (f: PermissionFunctionOrString) => {
                    if (_.isFunction(f)) {
                        return (f as PermissionFunction).query(id);
                    } else {
                        return f;
                    }
                }));
            });

            // The following permissions must always be assigned for creating anything
            permissions.push(
                this.options.permissionCollection.canViewUserGroup.query(this.options.currentUserGroup.get('id')),
                this.options.permissionCollection.canViewUserGroupPermissions.query(this.options.currentUserGroup.get('id')),
                this.options.permissionCollection.canModifyUserGroupPermissions.query(this.options.currentUserGroup.get('id'))
            );
        }

        this.getCollection().each((m) => {
            _.each(map.modelPermissions, (entry, i) => {
                var id = m.get('id');
                if (!this.getCheckbox(i, id).is(':checked')) return;

                // Add the permission that is directly controlled by the checkbox
                permissions.push(entry.func.query(id));

                _.each(entry.provides, (funcs, referenceKey) => {
                    var id = m.get(referenceKey);
                    [].push.apply(permissions, _.map(funcs, (f: PermissionFunctionOrString) => {
                        if (_.isFunction(f)) {
                            return (f as PermissionFunction).query(id);
                        } else {
                            return f;
                        }
                    }));
                    console.log(funcs, JSON.stringify(permissions, null, 4));
                });
            })
        });

        return _.uniq(permissions);
    }
}

export class InstallPermissionSectionView extends BasePermissionSectionView {

    getPermissionNameMap(model?) {
        var c = this.options.userGroupPermissionCollection;
        return {
            modelPermissions: [{
                name: 'View',
                func: c.canViewInstall.bind(c),
                info: "Selecting 'Yes' will allow this team to view a specific installation including its modules (and their status), sensors, and channels",
                provides: {
                    id: [
                        c.canViewSystemStatus.bind(c),
                        c.canViewInstallOverrides.bind(c)
                    ]
                }
            }, {
                name: 'View Settings',
                func: c.canViewSensorConfig.bind(c),
                info: "Selecting 'Yes' will allow this team to view the settings of a specific installation, such as a module's sampling frequency, the units of channels, and the BACnet/modbus configuration",
                requires: [
                    c.canViewInstall.bind(c)
                ],
                provides: {
                    id: [
                        c.canViewChannelConfig.bind(c),
                        c.canViewInstallLayout.bind(c)
                    ]
                }
            }, {
                name: 'Modify Settings',
                func: c.canModifySensorConfig.bind(c),
                info: "Selecting 'Yes' will allow this team to modify the settings of a specific installation, such as a module's sampling frequency, the units of channels, the BACnet/modbus configuration, and Layout",
                requires: [
                    c.canViewInstall.bind(c),
                    c.canViewSensorConfig.bind(c)
                ],
                provides: {
                    id: [
                        c.canModifyInstall.bind(c),
                        c.canModifyChannelConfig.bind(c),
                        c.canModifyInstallOverrides.bind(c),
                        c.canCreateInstallLayout.bind(c),
                        c.canModifyInstallLayout.bind(c),
                        c.canDeleteInstallLayout.bind(c)
                    ]
                }
            }]
        };
    }

    getTitle() {
        return 'Installation Permissions';
    }

    getFirstColumnName() {
        return 'Installation';
    }

    getFirstColumnValue(model) {
        return model.getDisplayName();
    }

    getCollection() {
        return new InstallCollection(this.options.installCollection.where({
            organization_id: this.options.currentOrganizationId
        }));
    }

    getUrlAddon() {
        return 'install';
    }
}

export class DashboardPermissionSectionView extends BasePermissionSectionView {

    getPermissionNameMap(model?) {
        var c = this.options.userGroupPermissionCollection;
        return {
            createPermission: {
                name: 'Team can create new dashboards',
                func: c.canCreateNewDashboard.bind(c)
            },
            modelPermissions: [{
                name: 'View',
                func: c.canViewDashboard.bind(c),
                info: "Selecting 'Yes' will allow this team to view a specific dashboard"
            }, {
                name: 'Modify & Delete',
                func: c.canModifyDashboard.bind(c),
                info: "Selecting 'Yes' will allow this team to rename and delete the dashboard and to add, remove, and modify the widgets on the dashboard",
                requires: [
                    c.canViewDashboard.bind(c)
                ],
                provides: {
                    id: [
                        c.canDeleteDashboard.bind(c)
                    ]
                }
            }]
        };
    }

    getTitle() {
        return 'Dashboard Permissions';
    }

    getFirstColumnName() {
        return 'Dashboard';
    }

    getFirstColumnValue(model) {
        return model.get('name');
    }

    getCollection() {
        return new DashboardCollection(this.options.dashboardCollection.where({
            organization_id: this.options.currentOrganizationId
        }));
    }

    getUrlAddon() {
        return 'dashboard';
    }
}

export class UserGroupPermissionSectionView extends BasePermissionSectionView {

    getTitle() {
        return 'Team Permissions';
    }

    getFirstColumnName() {
        return 'Team';
    }

    getFirstColumnValue(model) {
        return model.get('name');
    }

    getPermissionNameMap(model?) {
        var c = this.options.userGroupPermissionCollection;
        return {
            createPermission: {
                name: 'Team can create new teams',
                func: c.canCreateNewUserGroup.bind(c)
            },
            modelPermissions: [{
                name: 'Modify Permissions',
                func: c.canModifyUserGroupPermissions.bind(c),
                info: "Selecting 'Yes' will allow this team to modify the permissions of another team",
                provides: {
                    id: [
                        c.canViewUserGroup.bind(c),
                        c.canViewUserGroupPermissions.bind(c)
                    ]
                }
            }, {
                name: 'Invite Users',
                func: c.canAddUserToUserGroup.bind(c),
                info: "Selecting 'Yes' will allow this team to invite users to another team",
                provides: {
                    id: [
                        c.canViewUserGroup.bind(c),
                        c.canViewUserGroupPermissions.bind(c)
                    ]
                }
            }]
        };
    }

    getCollection() {
        return this.options.userGroupCollection;
    }

    getUrlAddon() {
        return 'team';
    }
}

export class ReportPermissionSectionView extends BasePermissionSectionView {

    getPermissionNameMap(model?) {
        var c = this.options.userGroupPermissionCollection;
        return {
            createPermission: {
                name: 'Team can create new reports',
                func: c.canCreateNewReport.bind(c)
            },
            modelPermissions: [{
                name: 'View',
                func: c.canViewReport.bind(c),
                info: "Selecting 'Yes' will allow this team to view a specific report",
                provides: {
                    'dashboard_id': [
                        c.canViewDashboard.bind(c)
                    ]
                }
            }, {
                name: 'Modify & Delete',
                func: c.canModifyReport.bind(c),
                info: "Selecting 'Yes' will allow this team to rename and delete the report and to add, remove, and modify the widgets on the report",
                requires: [
                    c.canViewReport.bind(c)
                ],
                provides: {
                    id: [c.canDeleteReport.bind(c)],
                    dashboard_id: [
                        c.canModifyDashboard.bind(c),
                        c.canDeleteDashboard.bind(c)
                    ]
                }
            }]
        };
    }

    getTitle() {
        return 'Report Permissions';
    }

    getFirstColumnName() {
        return 'Report';
    }

    getFirstColumnValue(model) {
        return model.get('name');
    }

    getCollection() {
        return new ReportsCollection(this.options.reportsCollection.where({
            organization_id: this.options.currentOrganizationId
        }));
    }

    getUrlAddon() {
        return 'report';
    }
}



// WEBPACK FOOTER //
// ./srv/public/js/views/account/team/PermissionView.ts